import {
  Component, OnInit, OnDestroy, Input, Output, EventEmitter,
  NgZone, Inject, ChangeDetectorRef,
  ElementRef, ViewChild, ViewChildren, QueryList,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable ,  Subscription } from 'rxjs';
import * as moment from 'moment-timezone';
import * as _ from 'lodash';

import {
  ACTION_CREATED, ACTION_UPDATED, ACTION_STATUS_CHANGED,
  ACTION_BULK_UPDATED, ACTION_ROLLED_BACK, ACTION_IMPORTED,
  ACTION_AUTO_FIX,
} from '../../logs-actions';
import {
  PRODUCT_STATUS_WORKING, PRODUCT_STATUS_REVIEW,
  PRODUCT_STATUS_APPROVED, PRODUCT_STATUS_PUBLISHED,
  PRODUCT_STATUS_UNPUBLISHED,
} from '../../../product/product-statuses';
import {
  OUT, HOVER, NEED_CONFIRM,
} from './rollback-statuses';
import { segments } from '../../../../product-details/segments-list';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';
import {takeWhile} from 'rxjs/operators';
import {
  BOT_USER_ID, BOT_USER_NAME,
} from '../../../user/user-helpers';


@Component({
  moduleId: module.id,
  selector: 'log-component',
  templateUrl: 'log.component.html',
  styleUrls: ['log.component.scss'],
})
export class LogComponent implements OnInit, OnDestroy {

  @Input() log: any;
  @Input() timezone: any = moment.tz.guess();
  @Input() statusRules: string[];
  @Input() statusLookup: any[];

  @Output() confirmRollback: EventEmitter<any> = new EventEmitter();
  @Output() segmentClick: EventEmitter<any> = new EventEmitter();

  @ViewChild('logItem', {static: false}) logItem: ElementRef | void;
  @ViewChildren('sequenceItem') seqItems: QueryList<ElementRef>;

  formatDateStr: string = 'MM/DD/YYYY hh:mm A';

  private isActive: boolean = true;
  private rollbackStatus: number = OUT;
  private docClickSubscription: Subscription;

  // sequence
  private seqExpanded: boolean = false;

  constructor(
    private ngZone: NgZone,
    private ref: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: any,
  ) {}

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get isChangedStatus(): boolean {
    if (_.has(this.log, 'action.action')) {
      return this.log.action.action === ACTION_STATUS_CHANGED;
    }
    return false;
  }

  get isChangedStatusUp(): boolean {
    if (_.has(this.log, 'action.action')) {
      return this.isChangedStatus &&
        (this.log.action.to - this.log.action.from) > 0 &&
        this.log.action.from !== PRODUCT_STATUS_PUBLISHED &&
        this.log.action.to !== PRODUCT_STATUS_UNPUBLISHED;
    }
    return false;
  }

  get isChangedStatusDown(): boolean {
    if (_.has(this.log, 'action.action')) {
      return this.isChangedStatus &&
        (this.log.action.to - this.log.action.from) < 0 ||
        (this.log.action.from === PRODUCT_STATUS_PUBLISHED &&
        this.log.action.to === PRODUCT_STATUS_UNPUBLISHED);
    }
    return false;
  }

  get title(): string {
    if (this.isCreated(this.log)) { return 'Created'; }
    if (this.isUpdated(this.log)) { return 'Updated segment'; }
    if (this.isBulkUpdated(this.log)) { return 'Bulk updated segment'; }
    if (this.isRolledBack(this.log)) { return 'Item has rolled back'; }
    if (this.isImported(this.log)) { return 'Updated by ' + this.importType + ' import'; }
    if (this.isChangedStatus) { return 'Updated status to'; }
    if (this.isAutoFix) { return 'Item auto fixed'; }
    return null;
  }

  get segment(): string {
    if (!this.log || !_.has(this.log, 'action.segment')) { return null; }
    const segment: any = _.find(segments, {id: _.get(this.log, 'action.segment')});
    return segment ? segment.title : null;
  }

  get status(): string {
    if (!this.log || !_.has(this.log, 'action.to')) { return null; }
    return this.getStatusName(_.get(this.log, 'action.to'));
  }

  get detail(): string {
    if (!this.log || !_.has(this.log, 'action.detail')) { return null; }
    return _.get(this.log, 'action.detail');
  }

  get userName(): string {
    if (_.get(this.log, 'user_id') === BOT_USER_ID) { return BOT_USER_NAME; }
    if (!_.get(this.log, 'user')) { return null; }
    let userName = _.get(this.log, 'user.name');
    if (!userName) {
      const firstName = _.get(this.log, 'user.first_name');
      const lastName = _.get(this.log, 'user.last_name');
      if (firstName && lastName) { userName = `${firstName} ${lastName}`; }
    }
    return userName ? userName : null;
  }

  // sequences
  get isSequence(): boolean {
    if (!_.has(this.log, 'innerLogs')) { return false; }
    return this.log.innerLogs.length > 1;
  }

  get seqHeight(): number {
    // one seqItem = 50px height
    return 50 * this.log.innerLogs.length;
  }

  get importType(): string {
    if (this.isImported(this.log)) {
      let importType: string =  _.get(this.log, 'action.type', 'products');
      if (importType.toLowerCase() === 'items') {
        importType = 'products';
      }
      return importType.toLowerCase();
    } else {
      return '';
    }
  }

  get statusClasses(): string {
    if (
      this.isCreated(this.log) ||
      this.isUpdated(this.log) ||
      this.isAutoFix(this.log) ||
      this.isBulkUpdated(this.log)
    ) {
      return 'updated';
    } else if (this.isImported(this.log)) {
      return 'imported';
    } else if (this.isRolledBack(this.log)) {
      return 'rolled-back';
    } else if (this.isChangedStatus) {
      return `status-changed ${this.getChangeStatusClasses()}`;
    }
    return 'updated';
  }

  get isFitmentsLog(): boolean {
    return _.get(this.log, 'action.segment') === 'fitments';
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

   ngOnInit() {
     this.ngZone.runOutsideAngular(() => {
       this.docClickSubscription = fromEvent(this.document, 'click')
       .pipe(takeWhile(() => this.isActive))
       .subscribe((event: MouseEvent) => {
         this.onDocumentClick(event);
       });
     });
   }

   ngOnDestroy() {
     this.isActive = false;
     if (this.docClickSubscription) { this.docClickSubscription.unsubscribe(); }
   }

   /*******************************************
    * COMPONENT PUBLIC METHODS
    *******************************************/

   public isCreated(log: any): boolean {
    if (_.has(log, 'action.action')) {
      return log.action.action === ACTION_CREATED;
    }
    return false;
   }

   public isUpdated(log: any): boolean {
    if (_.has(log, 'action.action')) {
      return log.action.action === ACTION_UPDATED;
    }
    return false;
   }

   public isBulkUpdated(log: any): boolean {
    if (_.has(log, 'action.action')) {
      return log.action.action === ACTION_BULK_UPDATED;
    }
    return false;
   }

   public isAutoFix(log: any): boolean {
    if (_.has(log, 'action.action')) {
      return log.action.action === ACTION_AUTO_FIX;
    }
    return false;
   }

   public isRolledBack(log: any): boolean {
    if (_.has(log, 'action.action')) {
      return log.action.action === ACTION_ROLLED_BACK;
    }
    return false;
   }

   public isImported(log: any): boolean {
    if (_.has(log, 'action.action')) {
      return log.action.action === ACTION_IMPORTED;
    }
    return false;
   }

   public getStatusName(statusNumber: number): string {
     // @ts-ignore
       return _.get(_.find(this.statusLookup, {id: statusNumber}), 'status', '');
   }

   // rollback statuses
   public isRollbackHover(log: any): boolean {
     if (!log || !log.rollbackStatus) { return false; }
     return log.rollbackStatus === HOVER;
   }

   public isRollbackNeedConfirm(log: any): boolean {
     if (!log || !log.rollbackStatus) { return false; }
     return log.rollbackStatus === NEED_CONFIRM;
   }

   public isAllowedRollback(log: any): boolean {
     return log ? log.rollback_id : false;
   }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onRollbackMouseOver(log: any) {
    if (log.rollbackStatus !== NEED_CONFIRM) {
      log.rollbackStatus = HOVER;
      this.ref.markForCheck();
    }
  }

  onRollbackMouseLeave(log: any) {
    if (log.rollbackStatus !== NEED_CONFIRM) {
      log.rollbackStatus = OUT;
      this.ref.markForCheck();
    }
  }

  onRollbackClick(log: any) {
    log.rollbackStatus = NEED_CONFIRM;
    this.ref.markForCheck();
  }

  onDocumentClick(event: any): void {
    if (!_.get(this.logItem, 'nativeElement')) {
      return;
    }
    if (this.logItem && !this.logItem.nativeElement.contains(event.target)) {
      this.log.rollbackStatus = OUT;
    }
    this.seqItems.forEach((seqItem: ElementRef) => {
      if (!_.get(seqItem, 'nativeElement')) {
        return;
      }
      if (!seqItem.nativeElement.contains(event.target)) {
        const index = seqItem.nativeElement.getAttribute('data-index');
        if (index >= 0 && this.log.innerLogs[index]) {
          this.log.innerLogs[index].rollbackStatus = OUT;
        }
      }
    });
    this.ref.markForCheck();
  }

  onConfirmRollbackClick(log: any) {
    this.confirmRollback.emit(log);
  }

  onSegmentClick() {
    if (_.has(this.log, 'action.segment')) {
      this.segmentClick.emit(_.get(this.log, 'action.segment'));
    }
  }

  onSequenceActionClick() {
    this.seqExpanded = !this.seqExpanded;
    this.ref.markForCheck();
  }

  private getChangeStatusClasses() {
    const statusFrom = _.get(this.log, 'action.from');
    const statusTo = _.get(this.log, 'action.to');
    let classes = '';

    if (this.statusRules) { // we have custom rules for status states, e.g. receivers
      const rule: any = _.find(this.statusRules, (_rule: any) => {
        if (_rule.to.indexOf(statusTo) >= 0) {
          if (!_rule.from) { return true; } // doesn't matter 'from'
          return _rule.from.indexOf(statusFrom) >= 0;
        }
        return false;
      });
      if (rule) {
        classes = `status-${rule.color} status-${rule.direction}`;
      }
    } else { // we have no custom rules for status states - it's part logs
      switch (statusTo) {
        case PRODUCT_STATUS_WORKING:
          classes = 'status-red';
          break;
        case PRODUCT_STATUS_REVIEW:
          classes = 'status-yellow';
          break;
        case PRODUCT_STATUS_APPROVED:
          classes = 'status-green';
          break;
        case PRODUCT_STATUS_PUBLISHED:
        case PRODUCT_STATUS_UNPUBLISHED:
          classes = 'status-blue';
          break;
        default:
          break;
      }
      if (this.isChangedStatusDown) {
        classes = `${classes} status-down`;
      } else {
        classes = `${classes} status-up`;
      }
    }

    return classes;
  }
}
